import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Loader2, Crown, Shield, Bell, Mail, Trash2, Archive, Eye, AlertTriangle } from "lucide-react";
import { useSuperuser } from "@/hooks/useSuperuser";
import { useSystemSettings } from "@/hooks/useSystemSettings";
import type { SystemSettings } from "@/hooks/useSystemSettings";
import { toast } from "sonner";

interface SettingToggleProps {
  settingKey: keyof SystemSettings;
  label: string;
  description: string;
  icon: React.ReactNode;
  destructive?: boolean;
  value: boolean;
  onToggle: (key: keyof SystemSettings, value: boolean) => Promise<void>;
}

function SettingToggle({ settingKey, label, description, icon, destructive, value, onToggle }: SettingToggleProps) {
  const handleChange = async (checked: boolean) => {
    try {
      await onToggle(settingKey, checked);
      toast.success(`${label} ${checked ? "enabled" : "disabled"}`);
    } catch {
      toast.error(`Failed to update ${label.toLowerCase()}`);
    }
  };

  return (
    <div className="flex items-start justify-between gap-4 py-4">
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 rounded-lg p-2 ${destructive ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}>
          {icon}
        </div>
        <div className="space-y-1">
          <Label htmlFor={settingKey} className="text-sm font-medium leading-none cursor-pointer">
            {label}
          </Label>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <Switch
        id={settingKey}
        checked={value}
        onCheckedChange={handleChange}
      />
    </div>
  );
}

export default function SuperuserSettings() {
  const { isSuperuser, loading: superuserLoading } = useSuperuser();
  const { settings, loading: settingsLoading, updateSetting } = useSystemSettings();

  if (superuserLoading || settingsLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isSuperuser) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">System Settings</h1>
          <p className="text-muted-foreground">Only superusers can access system settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            System Settings
            <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">
              <Crown className="h-3 w-3 mr-1" />
              Superuser Only
            </Badge>
          </h1>
          <p className="text-muted-foreground mt-1">
            Control sensitive system-wide features. Changes take effect immediately.
          </p>
        </div>
      </div>

      {/* Notification Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Controls
          </CardTitle>
          <CardDescription>
            Control which email notifications are sent by the system
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y">
          <SettingToggle
            settingKey="impersonation_notifications_enabled"
            label="Impersonation Notifications"
            description="Send email alerts to all admins when a user is being impersonated. Disabling this hides impersonation activity from email."
            icon={<Eye className="h-4 w-4" />}
            value={settings.impersonation_notifications_enabled}
            onToggle={updateSetting}
          />
          <SettingToggle
            settingKey="role_change_email_enabled"
            label="Role Change Emails"
            description="Send an email to the affected user when their role is changed by an admin."
            icon={<Shield className="h-4 w-4" />}
            value={settings.role_change_email_enabled}
            onToggle={updateSetting}
          />
          <SettingToggle
            settingKey="admin_action_email_enabled"
            label="Admin Action Alerts"
            description="Send email notifications to all admins for sensitive actions like role changes, user deletions, and critical operations."
            icon={<Mail className="h-4 w-4" />}
            value={settings.admin_action_email_enabled}
            onToggle={updateSetting}
          />
          <SettingToggle
            settingKey="broadcast_email_enabled"
            label="Broadcast Notifications"
            description="Allow sending broadcast push notifications to users. When disabled, the broadcast feature is completely blocked."
            icon={<Bell className="h-4 w-4" />}
            value={settings.broadcast_email_enabled}
            onToggle={updateSetting}
          />
        </CardContent>
      </Card>

      {/* Destructive Action Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Destructive Actions
          </CardTitle>
          <CardDescription>
            Control whether destructive operations are allowed system-wide
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y">
          <SettingToggle
            settingKey="user_deletion_enabled"
            label="User Deletion"
            description="Allow administrators to permanently delete user accounts. When disabled, the delete button is hidden for all admins."
            icon={<Trash2 className="h-4 w-4" />}
            destructive
            value={settings.user_deletion_enabled}
            onToggle={updateSetting}
          />
          <SettingToggle
            settingKey="log_cleanup_enabled"
            label="Log Cleanup"
            description="Allow manual log cleanup and purging operations. When disabled, the cleanup button on the Log Retention page is blocked."
            icon={<Archive className="h-4 w-4" />}
            destructive
            value={settings.log_cleanup_enabled}
            onToggle={updateSetting}
          />
        </CardContent>
      </Card>
    </div>
  );
}
